import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useContext,
} from 'react'

// constants
import {
  mappedEntryPointNames,
  entryPointId,
  // searchTypeIds,
  PAGE_TYPES,
  __dev__,
  __prod__,
  BOOKABLE_TYPES,
  SHOW_SINGLE_QSR_INDICATORS,
  RESULT_HAS_DETAIL_PROFILE,
  NAVI_TYPES,
  __local_cdn__,
  GOOGLE_API_KEY,
} from 'settings/app/constants'

// component stack
import * as Styled from './Main.styles'
import * as Component from 'components/molecules/SearchBar/SearchBar.styles'
import {
  Grid,
  Loader,
  Tooltip,
  Link,
  FactsBox,
  ContentSection,
  SubHeading,
} from 'components/atoms'
import {
  ResultsHeader,
  ZeroResults,
  ResultsFooter,
  ResultInfoText,
  Map,
  MessageBox,
  ResultSorting,
  Services,
  Modal,
  ResultTile,
  StructuredData,
} from 'components/molecules'
import { ResultList, CrossResultList } from 'components/organisms'
import { Filters as FilterContainer, MobileMenu, Search } from './partials/'
import { Chip, ChipList } from 'components/molecules/Filter/partials'

// higher order components
import {
  ErrorBoundary,
  UrlParser,
  DistanceCalculation,
  TextContext,
  useCostEstimate,
} from 'hocs'

// redux && router
import { toggleBookmark } from 'redux/actions/bookmarkActions'
import { useSelector, useDispatch } from 'react-redux'
import { withRouter, Link as RouterLink } from 'react-router-dom'
import { searchHandler, getMoreResults } from 'redux/actions/searchActions'
import {
  resetFilterCollection,
  setFilterSelection,
  setSortingSelection,
} from 'redux/actions/filterActions'

// helpers
import _, { uniqBy } from 'lodash'
import qs from 'query-string'
import DOMPurify from 'dompurify'
import { useMobileSwitch, useRedirects, usePrintResultTiles } from 'hooks'
import { getHeaderHeight } from 'settings/app/uiHelpers'
import {
  getReplacedString,
  searchRecursiveForDuplicate,
  getRadiusOptions,
} from 'settings/app/helpers'
import {
  getDetailURL,
  getSearchUrl,
  getPlainComparePath,
  getKoroDeepLink,
  getSearchGetParams,
} from 'settings/app/urlHelpers'
import { useHistory } from 'react-router'

/**
 * main view (result list)
 * @returns {JSX.Element}
 * @constructor
 */
const Main = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const isMobile = useMobileSwitch()
  const textContext = useContext(TextContext)
  const { handleCostEstimate } = useCostEstimate()

  const { apiError, application, bookmarks, filter, search } = useSelector(
    (state) => state
  )
  const { entryPoint, naviType } = application
  const { sorting, activeSort, collection: filterCollection } = filter
  const { query, location, radius } = search.params

  // TODO CHECK LATER activeSort === currentSort
  // const currentSort = useSelector((state) => state.filter.activeSort)

  /**
   * get search params
   */
  const {
    noResults,
    generalTooltips,
    generalHints,
    factsBox,
    services,
    showDetails,
    size,
    clientHasSearched,
    isAop,
    isTreatmentReason,
    count,
    results: searchResults,
    prevParams,
    qsrIdentifier,
  } = search

  const searchLocation = search?.location?.source || {}
  const [params, setParams] = React.useState({
    query: search.params.query,
    location: search.params.location,
    radius: search.params.radius,
  })

  // local states
  const [isLoading, setIsLoading] = useState(false)
  const [openAll, setOpenAll] = useState(false)
  const [activeItem, setActiveItem] = useState(null)
  const [activeItemType, setActiveItemType] = useState(null)
  const [isMapFullscreen, setIsMapFullscreen] = useState(false)
  const [isFilterFullscreen, setIsFilterFullscreen] = useState(false)
  const [loadMore, setLoadMore] = useState(false)
  const [pageType, setPageType] = useState(null)
  const [isScrolling, setIsScrolling] = useState(false)
  const typoHeaderHeight = getHeaderHeight()
  // for overwrite messageBox render state
  const [showHints, setShowHints] = useState({
    aopHint: true,
    koroHint: true,
  })
  const [comparable, setComparable] = useState(false)
  const redirectURLs = useRedirects()

  // print result tiles
  const { onPrintBtnClicked } = usePrintResultTiles({
    openAll,
    setOpenAll,
    setIsLoading,
  })

  // refs
  const resultContainerRef = useRef()

  // render flags
  const renderResults = 0 < count // just for result rendering
  const renderFilter = !isMobile && (renderResults || noResults)
  const renderInitialLoader = isLoading && !clientHasSearched
  const renderResultHeader = true // currently deactivated. See: https://bugs.onedev.aok.de/browse/MGMPMNAV-1537
  const appendHandleHover = entryPointId.MIDWIVES !== entryPoint && !isScrolling
  const hideResultTileAccordionToggle = !(
    (entryPoint === entryPointId.HOSPITALS &&
      renderResults &&
      !isAop &&
      typeof qsrIdentifier !== 'undefined' &&
      qsrIdentifier.length === 0 &&
      prevParams.query === '') ||
    entryPoint === entryPointId.AMBULANCES ||
    entryPoint === entryPointId.MIDWIVES ||
    naviType === NAVI_TYPES[1] ||
    activeSort === 'best_result'
  )
  // wraps the entire components below searchBar (for Example, map, filter, sorts, resultsList) [modified render flag for production Mode]
  const renderEntireResultComponents = __prod__
    ? PAGE_TYPES.RESULT_PAGE === localStorage.getItem('pageType') ||
      PAGE_TYPES.SEO_PAGE === localStorage.getItem('pageType')
    : true

  /**
   * scroll top on load
   */
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [])

  /**
   * memorize search handler (unique function reference)
   * @type {(function(*=, *=): void)|*}
   */
  const onPerformSearch = useCallback(
    (searchTyp, params = false) => {
      setIsLoading(true)
      if (!params) {
        params = { query, location, radius }
      }

      dispatch(
        searchHandler(searchTyp, params, (url) => {
          // MGMNAVSUP-129
          if (pageType !== PAGE_TYPES.SEO_PAGE) {
            history.push(url)
          }

          setParams(params)
          setIsLoading(false)
        })
      )
    },
    [history, query, location, radius, dispatch, pageType]
  )

  /**
   *  set page Type
   *  used for seo behavior
   */
  useEffect(() => {
    if (__dev__ && !window?.Cypress) {
      setPageType(PAGE_TYPES.RESULT_PAGE)
    } else {
      setPageType(localStorage.getItem('pageType'))
      if (pageType === PAGE_TYPES.SEO_PAGE) {
        if (!isMobile) toggleMapFullscreen(true)
      }
    }
  }, [pageType, renderResults, isMobile])

  /**
   * set general hints show values (always open after new search)
   * for aop and koro
   */
  useEffect(() => {
    setShowHints({
      aopHint: true,
      koroHint: true,
    })
  }, [searchResults])

  // toggle resultTile (all) state (open||closed)
  useEffect(() => {
    setOpenAll(showDetails)
  }, [showDetails])

  // check whether mobile compare button should be rendered
  useEffect(() => {
    let isComparable = false
    // check if entryPoint is comparable and whether enough bookmarks are set for compare
    if (
      bookmarks.types.hasOwnProperty(entryPoint) &&
      bookmarks.types[entryPoint].count >= 2
    ) {
      isComparable = true
    }
    setComparable(isComparable)
  }, [entryPoint, bookmarks])

  /**
   * increase radius handler (used at ZeroResults)
   * @param newRadius
   */
  const handleIncreaseRadius = (newRadius) => {
    onPerformSearch('increaseRadius', { ...params, radius: newRadius })
  }

  // hover state for result tile
  const handleHover = (onHover, index, tileTyp) => {
    if (isScrolling) return false

    if (onHover) {
      setActiveItem(index.toString())
      setActiveItemType(tileTyp)
    } else {
      setActiveItem(null)
      setActiveItemType(null)
    }
  }

  const toggleMapFullscreen = (bool) => {
    setIsMapFullscreen(bool)
    if (!bool) setActiveItem(null)
  }

  const handleScroll = () => {
    setIsScrolling(true)

    setTimeout(() => {
      setIsScrolling(false)
    }, 1000)
  }

  const handleMarkerClick = (index) => {
    if (!isMapFullscreen) {
      const itemEl = document.getElementById(`result-tile-${ index }`)

      handleScroll()
      let scrollTop =
        itemEl.getBoundingClientRect().top +
        document.documentElement.scrollTop -
        20

      if (typoHeaderHeight > 0) {
        scrollTop -= typoHeaderHeight
      }

      window.scroll({
        behavior: 'smooth',
        top: scrollTop,
      })

      setActiveItem(index.toString())
    }
  }

  /**
   * handle load more and scroll after new results
   */
  const handleLoadMoreResults = () => {
    // use number of search results for from
    const numberOfResultTiles = Object.keys(searchResults).length
    const curLastetResult = document.getElementById('resultlist').lastChild
    setLoadMore(true)

    dispatch(getMoreResults({ from: numberOfResultTiles, size }))
      .then((url) => {
        history.push(url)
        setLoadMore(false)
      })
      .then(() => {
        const y =
          curLastetResult.getBoundingClientRect().top +
          window.pageYOffset -
          typoHeaderHeight
        window.scrollTo({ top: y, behavior: 'smooth' })
      })
  }

  /**
   * parse filter state and map filter data
   * @returns {[][]}
   */
  const getActiveActiveChips = () => {
    const filterChips = []
    const duplicates = []

    Object.keys(filterCollection).forEach((item, index) => {
      const filter = filterCollection[index]

      if ('boolean' === filter.type) {
        if (filter.selected) {
          // skip chip if filter has a duplicate and add to duplicate array
          // its important to render chips (if duplicate exits with parentKey (for reset))
          if (
            !searchRecursiveForDuplicate(filterCollection, filter.key, {
              parentKey: false,
              index,
            }) &&
            !duplicates.includes(filter.key)
          ) {
            filterChips.push(
              mapFilterToChip({
                filter,
                index,
                parentKey: false,
              })
            )
          } else {
            duplicates.push(filter.key)
          }
        }
      } else if (
        ('group' === filter.type || 'splitted_group' === filter.type) &&
        0 < filter.options.length
      ) {
        if ('custom_opening_hours' === filter.key) {
          if (
            '' !== filter.options[0].selected &&
            '' !== filter.options[1].selected
          ) {
            filterChips.push(
              mapFilterToChip({
                filter: {
                  key: filter.key,
                  label: filter.label,
                  chip_label: filter.chip_label,
                  tip: filter.tip,
                },
                index,
                parentKey: filterCollection[index].key,
              })
            )
          }
        } else {
          filter.options.forEach((groupedFilter, groupedIndex) => {
            if (groupedFilter.selected) {
              if (
                groupedFilter.isDuplicate === groupedFilter.key &&
                !duplicates.includes(groupedFilter.key)
              ) {
                duplicates.push(groupedFilter.key)
                return true
              }

              filterChips.push(
                mapFilterToChip({
                  filter: groupedFilter,
                  index: `${ index }-${ groupedIndex }`,
                  parentKey: filterCollection[index].key,
                })
              )
            }
          })
        }
      }
    })

    return filterChips
  }

  /**
   * analogue to handleFilterSelect fn @ filter container
   * removes a filter with click at the chip
   */
  const handleChipSelection = ({ filter, parentKey }) => {
    setIsLoading(true)

    let filterObject = { filter, parentKey }
    // set time filter to initial values
    if ('custom_opening_hours' === filter) {
      filterObject = { ...filterObject, opening_day: '', opening_time: '' }
    }

    dispatch(
      setFilterSelection('desktopFilter', filterObject, (url) => {
        history.push(url)
        setIsLoading(false)
      })
    )
  }

  /**
   * get general search hints
   */
  const getGeneralHints = (resultHints = false) => {
    const hints = !resultHints ? generalHints : resultHints
    return hints.map((hint, index) => {
      // remove cross condition if more general hints should display on cross search
      if (hint.redirect === null) {
        return <MessageBox key={ index } messageText={ hint.text } />
      }

      // AOP MESSAGE
      if (hint.redirect === 'doctor-search') {
        return (
          <MessageBox
            key={ index }
            data-cy='aop-hint'
            handleShow={ () =>
              setShowHints((prevShowHints) => ({
                ...prevShowHints,
                aopHint: false,
              }))
            }
            show={ showHints.aopHint }>
            <div className='message-box-content'>
              <p>{hint.text.replace('_SEARCH_TERM_', prevParams.query)}</p>
              <Link
                isNativeLink
                href={ `${ redirectURLs.doctors }${ getSearchUrl(params) }` }
                data-cy='aop-redirect-link'>
                {textContext['searchHospital.messageBox.AopLink']}
              </Link>
            </div>
          </MessageBox>
        )
      }

      // KORO MESSAGE
      if (hint.redirect === 'link-target-koro') {
        return (
          <MessageBox
            key={ index }
            data-cy='koro-hint'
            handleShow={ () =>
              setShowHints((prevShowHints) => ({
                ...prevShowHints,
                koroHint: false,
              }))
            }
            show={ showHints.koroHint }>
            <div className='message-box-content'>
              <p
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    hint.text.replace(
                      '_HIER_',
                      getKoroDeepLink({
                        redirectURL: redirectURLs[entryPointId.HOSPITALS],
                        koroParams: { ...qs.parse(window.location.search) },
                      })
                    )
                  ),
                }}
              />
            </div>
          </MessageBox>
        )
      }

      return false
    })
  }

  /**
   * map filter data to component
   * @param filter
   * @param index
   * @param parentKey
   * @returns {*}
   */
  const mapFilterToChip = ({ filter, index, parentKey }) => (
    <Chip
      key={ index }
      chipId={ filter.key }
      isLoading={ isLoading }
      tooltip={
        null !== filter.tip &&
        undefined !== filter.tip && (
          <Tooltip
            arrow='left'
            infoIconId={ `${ filter.label }-chip-icon-id` }
            tooltipId={ `${ filter.label }-chip-tip-id` }
            dialogTitle={ filter.label }>
            {filter.tip}
          </Tooltip>
        )
      }
      category={
        null !== filter.chip_label && undefined !== filter.chip_label
          ? filter.chip_label
          : filter.label
      }
      onClose={ (filter) =>
        handleChipSelection({
          filter,
          parentKey,
        })
      }
    />
  )

  /**
   * sort handler && url manipulation
   * @param sortBy
   */
  const handlerSortChange = (sortBy) => {
    setIsLoading(true)

    if (sortBy === 'best_result') {
      setOpenAll(true)
    } else {
      setOpenAll(false)
    }

    dispatch(
      setSortingSelection(sortBy, (url) => {
        history.push(url)
        setIsLoading(false)
      })
    )
  }

  /**
   * get mapped result tiles
   * @param resultCollection
   * @param currentSort (qss can still have different sorting, provide sorting for each result type )
   * @param typ
   * @returns {*[]|unknown[]}
   */
  const getResults = ({ resultCollection, currentSort, typ = false }) =>
    resultCollection !== null || typeof resultCollection === 'object'
      ? Object.keys(resultCollection).map((key) => {
          const result = resultCollection[key]
          let tileTyp = typ
          let isBooked, activeResultItem
          const linksProp = {} // only pass Links for ResultTiles that are supposed to have detail views

          // map booking state (oncallservices && ambulances && midwives have no booking state)
          switch (entryPoint) {
            case entryPointId.CROSS:
              switch (typ) {
                case 'asv_teams':
                  // if resultTile renders as asv or doctor this distinction is handled at tile
                  tileTyp = 'doctors'
                  isBooked = false
                  break

                case 'ambulances':
                  isBooked = false
                  break

                default:
                  isBooked =
                    bookmarks.types[typ]?.items.indexOf(result.id.toString()) >
                    -1
              }
              break
            case entryPointId.ONCALLSERVICES:
            case entryPointId.AMBULANCES:
              isBooked = false
              break
            default:
              isBooked =
                bookmarks.types[entryPoint]?.items.indexOf(
                  result.id.toString()
                ) > -1
          }

          if (tileTyp !== entryPointId.DOCTORS) {
            activeResultItem = activeItem === key && activeItemType === tileTyp
          } else {
            activeResultItem =
              activeItem === key && activeItemType === result.docType
          }

          const type = entryPointId.CROSS === entryPoint ? tileTyp : entryPoint
          const resultType =
            entryPointId.CROSS === entryPoint ? typ : entryPoint

          if (RESULT_HAS_DETAIL_PROFILE.includes(type)) {
            const detailURL = getDetailURL({
              id: result.id,
              entryPoint: resultType,
              ...getSearchGetParams({
                resultType,
                prevParams: search.prevParams,
              }),
              city: search.location.source?.name,
              zip: search.location.source?.zip_code,
              lat: search.searchLocation?.lat,
              lng: search.searchLocation?.lng,
            })

            linksProp.linkComponent = RouterLink
            linksProp.linkComponentProps = {
              to: detailURL,
            }
          }

          /**
           * show dynamic part
           * @returns {boolean}
           */
          const showDataRows = () => {
            // https://bugs.onedev.aok.de/browse/MGMNAVSUP-1219
            if (
              type === entryPointId.CAREHOMES &&
              result?.contribution === null
            ) {
              return false
            }

            if (type !== entryPointId.CARESERVICES) {
              return true
            }

            return (
              result?.transparencyReport !== null ||
              (result?.contribution &&
                Array.isArray(result?.contribution) &&
                result?.contribution.find(
                  (contribution) => contribution.contractId !== null
                ) !== undefined)
            )
          }

          return (
            <ResultTile
              key={ key }
              data-cy='result-tile'
              id={ `result-tile-${ key }` }
              resultId={ result.id }
              isMobile={ isMobile }
              data-cy-result-tile-key={ key }
              { ...linksProp }
              // sorting best_results open all tile initial
              open={ openAll || currentSort === 'best_result' }
              active={
                entryPointId.CROSS !== entryPoint
                  ? activeItem === key
                  : activeResultItem
              }
              type={ type }
              is_treatment_reason={ isTreatmentReason }
              is_aop={ isAop }
              setBooked={ () =>
                dispatch(
                  toggleBookmark({
                    id: result.id,
                    type: entryPoint === entryPointId.CROSS ? typ : entryPoint,
                    searchParams: {
                      ...getSearchGetParams({
                        resultType: type,
                        prevParams: search.prevParams,
                      }),
                    },
                  })
                )
              }
              handleHover={ (onHover) =>
                appendHandleHover &&
                handleHover(
                  onHover,
                  key,
                  tileTyp !== entryPointId.DOCTORS ? tileTyp : result.docType
                )
              }
              generalTooltips={ generalTooltips }
              qsrIdentifier={ [...qsrIdentifier] }
              currentSort={ currentSort }
              searchKey={ prevParams.key }
              singleQSRIndicator={ [...SHOW_SINGLE_QSR_INDICATORS] }
              isBooked={ isBooked }
              isBookable={ BOOKABLE_TYPES.includes(type) }
              result={ result }
              showDataRows={ showDataRows() }
              handleCostEstimate={ handleCostEstimate }
            />
          )
        })
      : []

  /**
   * get mapped map
   * @returns {*}
   */
  const getMap = () => (
    <ErrorBoundary>
      <Map
        id='map'
        data-cy='map'
        apiKey={ GOOGLE_API_KEY }
        defaultCenter={{
          lat: searchLocation.lat || 51.05,
          lng: searchLocation.lng || 13.7,
        }}
        markers={ Object.keys(searchResults).reduce((markers, key) => {
          const result = searchResults[key]
          if (result.coordinates === null) return markers
          markers.push({
            lat: result.coordinates.lat,
            lng: result.coordinates.lon,
            title:
              'undefined' !== typeof result.locationName
                ? result.locationName
                : result.name,
            street: result.address.street,
            zip: result.address.zip,
            city: result.address.city,
            phone: result.phone,
            active: activeItem === key,
            detailUrl: result.id
              ? getDetailURL({
                  id: result.id,
                  entryPoint,
                  city: search.location.source
                    ? search.location.source.name
                    : null,
                  zip: search.location.source
                    ? search.location.source.zip_code
                    : null,
                  key: search.params.key,
                  type: search.params.type,
                  searchTerm: search.params.query,
                })
              : null,
          })
          return markers
        }, []) }
        radius={ radius ? radius : null }
        isMapFullscreen={ isMapFullscreen }
        onMarkerClicked={ (index) => handleMarkerClick(index) }
        onMarkerHovered={ handleHover }
        onToggleFullscreen={ (bool) => toggleMapFullscreen(bool) }
        mapPosition='resultList'
        height={ isMapFullscreen && !isMobile ? '570px' : '390px' }
      />
    </ErrorBoundary>
  )

  /**
   * * filter reset && url manipulation
   */
  const handleFilterReset = () => {
    setIsLoading(true)
    dispatch(
      resetFilterCollection((url) => {
        history.push(url)
        setIsLoading(false)
      })
    )
  }

  /**
   * get a loader instance
   **/
  const getLoaderWithMessage = (search) => (
    <Loader variant='loading' size='big'>
      {getReplacedString({
        search,
        replaceWith: mappedEntryPointNames[entryPoint],
      })}
    </Loader>
  )

  /**
   * get zeroResults with handlers
   * */
  const getZeroResults = () => {
    const zeroResultsMessage =
      entryPoint === entryPointId.CROSS && typeof params.query !== 'undefined'
        ? getReplacedString({
            search: textContext['zeroResults.text.qs'],
            replaceWith: mappedEntryPointNames[entryPoint],
          }).replace('__QUERY__TERM__', params.query)
        : getReplacedString({
            search: textContext['zeroResults.text'],
            replaceWith: mappedEntryPointNames[entryPoint],
          })

    const availableRadii = getRadiusOptions(entryPoint).map(
      (option) => option.value
    )

    return (
      <ZeroResults
        radii={ availableRadii }
        infoText={ zeroResultsMessage }
        onIncreaseRadiusClicked={ handleIncreaseRadius }
        onResetFiltersClicked={
          entryPoint !== entryPointId.CROSS ? () => handleFilterReset() : null
        }
        radius={ radius }
      />
    )
  }

  /**
   * return compare view url for current entryPoint if criteria is met
   **/
  const getCheckCompareLink = () => {
    if (
      bookmarks.types.hasOwnProperty(entryPoint) &&
      bookmarks.types[entryPoint].items.length >= 2
    ) {
      return getPlainComparePath(entryPoint, 'local')
    }

    return ''
  }

  // before open tiles, check first tile if they have accordion body with children
  const handleToggleAllTiles = () => {
    const resultListFirstChild =
      window.document.getElementById('resultlist')?.firstChild

    if (
      resultListFirstChild &&
      resultListFirstChild.getAttribute('data-cy') === 'result-tile'
    ) {
      if (
        resultListFirstChild.querySelector('div[data-cy="accordion-body"]')
          ?.children.length > 0
      ) {
        setOpenAll(!openAll)
      }
    }
  }

  return (
    <UrlParser onPerformSearch={ onPerformSearch }>
      <Styled.MainContainer>
        <Search
          performSearchRequest={ (params) =>
            onPerformSearch('initialSearch', params)
          }
          redirectURLs={ redirectURLs }
          pageType={ pageType }
        />

        {/* complete result section */}
        {renderEntireResultComponents && (
          <>
            {renderInitialLoader && (
              <>
                {apiError && <MessageBox isError={ true } />}
                {!apiError && (
                  <Grid item xs={ 12 } md={ 12 } lg={ 12 } xl={ 12 }>
                    {getLoaderWithMessage(
                      textContext['searchMain.loadMore.LoadingMessage']
                    )}
                  </Grid>
                )}
              </>
            )}

            {/* cross search (rendering the first four results from all result types) */}
            {clientHasSearched && entryPointId.CROSS === entryPoint && (
              <>
                {!isLoading && <ResultInfoText />}

                {/* check if hospital results are included and search query has a aop hit */}
                {renderResults &&
                  !isLoading &&
                  Object.keys(searchResults).map((index) =>
                    searchResults[index].typ === 'hospitals'
                      ? getGeneralHints(searchResults[index].generalHints)
                      : false
                  )}

                {/* health facts */}
                {renderResults && factsBox.length > 0 && !isLoading && (
                  <ContentSection
                    data-cy-facts-section='facts-section'
                    title={ getReplacedString({
                      search: textContext['factBox.section.title'],
                      replaceWith: factsBox.length,
                    }) }>
                    {uniqBy(factsBox, 'title').map((fact, index) => (
                      <FactsBox
                        data-cy-facts-section={ `${ fact.title }` }
                        key={ index }
                        title={ fact.title }
                        text={ fact.text }
                        link={ fact.link }
                      />
                    ))}
                  </ContentSection>
                )}

                {isLoading && (
                  <>
                    {apiError && <MessageBox isError={ true } />}
                    {!apiError &&
                      getLoaderWithMessage(
                        textContext['searchMain.loadMore.LoadingMessage']
                      )}
                  </>
                )}

                {renderResults && !isLoading && (
                  <div data-cy='resultlist'>
                    {Object.keys(searchResults).map((index) => {
                      const { typ, filters } = searchResults[index]
                      let activeFilters = ''
                      const filterCollection = []

                      if (searchResults[index]?.filters && filters.length > 0) {
                        filters.forEach((filter, index) => {
                          activeFilters += `${ filter.label }${
                            index + 1 !== filters.length ? ', ' : ''
                          }`
                          filterCollection.push(filter)
                        })
                      }

                      return (
                        <CrossResultList
                          key={ index }
                          type={ typ }
                          open={ searchResults[index].collapsed }
                          activeFilter={
                            activeFilters !== '' ? activeFilters : false
                          }
                          filterCollection={ filterCollection }
                          activeSort={
                            textContext[
                              `crossResultContainer.sorting.${ searchResults[index].active_sorting }`
                            ]
                          }
                          numberOfResults={ searchResults[index].count }
                          redirectURLs={ redirectURLs }
                          tooltip={
                            searchResults[index].hasOwnProperty('tooltip')
                              ? searchResults[index].tooltip
                              : false
                          }>
                          {getResults({
                            resultCollection: searchResults[index].results,
                            typ,
                            currentSort: searchResults[index].active_sorting,
                          })}
                        </CrossResultList>
                      )
                    })}
                  </div>
                )}

                {!renderResults && !isLoading && getZeroResults()}
              </>
            )}

            {/* typ specific search - rendering only one type */}
            {clientHasSearched && entryPointId.CROSS !== entryPoint && (
              <div
                id='resultListContainer'
                className='result-list-container'
                ref={ resultContainerRef }
                data-cy={ pageType }>
                <ResultInfoText />

                {generalHints && getGeneralHints()}

                {isMapFullscreen && (
                  <Grid container>
                    <Grid item xs={ 12 } sm={ 12 } md={ 12 } lg={ 12 } xl={ 12 }>
                      {(!isMobile || isMapFullscreen) && getMap()}
                    </Grid>
                  </Grid>
                )}

                {/* map in fullscreen mode (mobile and detail views [doctors && hospitals]) */}
                {isFilterFullscreen && isMobile && (
                  <Modal isFullScreen={ true } onClickCloseModal={ () => {} }>
                    <Component.ModalRow>
                      <FilterContainer
                        isFilterFullscreen={ isFilterFullscreen }
                        closeFilter={ () =>
                          setIsFilterFullscreen(!isFilterFullscreen)
                        }
                        setLoadingState={ (bool) => setIsLoading(bool) }
                        showSortOnMobile={ renderResultHeader }
                        resultContainerRef={ resultContainerRef }
                        typoHeaderHeight={ typoHeaderHeight }
                      />
                    </Component.ModalRow>
                  </Modal>
                )}

                {/* desktop => left sidebar with map and filter component */}
                <Grid
                  container
                  id='resultContainer'
                  spacing={ 6 }
                  className={ `result-container ${
                    !renderResults && !isMobile
                      ? 'result-container-padding'
                      : ''
                  }` }>
                  {renderFilter && (
                    <Grid item td={ 4 } lg={ 4 } xl={ 4 } className='print-hide'>
                      {entryPointId.MIDWIVES !== entryPoint &&
                        !isMapFullscreen &&
                        getMap()}

                      {Object.keys(filterCollection).length > 0 && (
                        <FilterContainer
                          isLoading={ isLoading }
                          setLoadingState={ (bool) => setIsLoading(bool) }
                          resultContainerRef={ resultContainerRef }
                          typoHeaderHeight={ typoHeaderHeight }
                          entryPoint={ entryPoint }
                        />
                      )}
                    </Grid>
                  )}

                  {/* desktop => result and information rendering */}
                  <Grid
                    item
                    xs={ 12 }
                    sm={ 12 }
                    md={ 12 }
                    td={ 8 }
                    lg={ 8 }
                    xl={ 8 }
                    className='print-flex-full-width'>
                    {!isMobile && renderResultHeader && (
                      <div
                        className={ `result-header ${
                          !hideResultTileAccordionToggle ? 'no-toggle' : ''
                        }`.trim() }>
                        <>
                          {hideResultTileAccordionToggle && (
                            <ResultsHeader
                              onOpenAllClicked={ () => handleToggleAllTiles() }
                              openAll={ openAll }
                              hideControls={ !renderResults }
                            />
                          )}
                          {sorting.length > 1 && renderResults && (
                            <ResultSorting
                              activeSort={ activeSort }
                              handlerSortChange={ (sortBy) =>
                                handlerSortChange(sortBy)
                              }
                              isLoading={ isLoading }
                              sorting={ sorting }
                              label={ textContext['searchMain.sortBy.label'] }
                              excludeCurrentValue
                            />
                          )}
                        </>
                      </div>
                    )}

                    {/* chip list component (based on filterCollection) */}
                    {Object.keys(filterCollection).length > 0 && (
                      <ChipList chipListId='chipListId'>
                        {getActiveActiveChips()}
                      </ChipList>
                    )}

                    {noResults &&
                      !isLoading &&
                      (!apiError ? (
                        getZeroResults()
                      ) : (
                        <MessageBox isError={ true } />
                      ))}

                    {!apiError &&
                      isLoading &&
                      getLoaderWithMessage(
                        textContext['searchMain.loadMore.LoadingMessage']
                      )}

                    {renderResults && !isLoading && (
                      <>
                        <ResultList id='resultlist' data-cy='resultlist'>
                          <DistanceCalculation>
                            {getResults({
                              resultCollection: searchResults,
                              currentSort: activeSort,
                            })}
                          </DistanceCalculation>
                        </ResultList>

                        {/* show error if no search results */}
                        {!loadMore && apiError && <MessageBox isError={ true } />}

                        {loadMore &&
                          getLoaderWithMessage(
                            textContext['searchMain.loadMore.PaginationMessage']
                          )}

                        <ResultsFooter
                          showLoadMoreButton={ _.size(searchResults) < count }
                          onLoadMoreClicked={ handleLoadMoreResults }
                          onPrintBtnClicked={ onPrintBtnClicked }
                          onPrintOpenAll={ (activatePrint) =>
                            setOpenAll(activatePrint)
                          }
                        />

                        {isMobile && (
                          <MobileMenu
                            toggleMapFullscreen={ toggleMapFullscreen }
                            setIsFilterFullscreen={ setIsFilterFullscreen }
                            isFilterFullscreen={ isFilterFullscreen }
                            comparable={ comparable }
                            bookmarkCount={ bookmarks.totalCount }
                            onBookmarkClick={ () =>
                              history.push({
                                pathname: '/merkliste',
                                state: { lastEntryPoint: entryPoint },
                              })
                            }
                            onCompareClick={ () => {
                              history.push(getCheckCompareLink())
                            } }
                          />
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              </div>
            )}
            {services && services.length > 0 && (
              <>
                <SubHeading>{textContext['subheading.services']}</SubHeading>
                <Services services={ services } />
              </>
            )}
          </>
        )}
      </Styled.MainContainer>
      {pageType === PAGE_TYPES.SEO_PAGE && (
        <StructuredData
          pageType={ pageType }
          entryPoint={ entryPoint }
          searchResults={ Object.keys(searchResults).map(
            (key) => searchResults[key]
          ) }
        />
      )}
    </UrlParser>
  )
}

export default withRouter(Main)
